import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CustomRadioCheck from "../../common/styles/components/checkboxs/CustomRadioCheck";
import WPSlider from "../WPSlider";
import WPInputTooltipError from "../WPInputTooltipError/WPInputTooltipError";
import { WPTypography, typographyVariants } from "../../packages/WPTypography";
import { ArrowRightIcon } from "../../assets/icons";
import { injectValues, truncateFloat } from "../../utils/appUtils";
import { InputStrings } from "../../utils/StringConstants";
import "./SliderCard.scss";
import { isNullOrUndefinedOrEmpty } from "../../utils/Utils";

const SliderCard = ({
  id,
  label,
  showCheckbox = false,
  checked = false,
  onCheckChange,
  inputValue,
  onChange,
  min,
  max,
  isInteger,
  recommended,
  maxLength,
  disabled = false,
  checkboxDisabled = false,
  showDivider = false,
  leftLabel,
  rightLabel,
}) => {
  const { t } = useTranslation();
  const [sliderVal, setSliderVal] = useState(inputValue);
  const numericMin = Number(min);
  const numericMax = Number(max);
  const formattedMin = Number(truncateFloat(Number(min), 2)).toFixed(2);
  const formattedMax = Number(truncateFloat(Number(max), 2)).toFixed(2);

  const validateSliderRange = (value) => {
    if (isNullOrUndefinedOrEmpty(value)) return null;

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return null;

    if (numericValue < numericMin || numericValue > numericMax) {
      return {
        error: injectValues(InputStrings(t).outOfRange, {
          min: formattedMin,
          max: formattedMax,
        }),
      };
    }

    return null;
  };

  useEffect(() => {
    setSliderVal(inputValue === "" ? "" : Number(inputValue));
  }, [inputValue]);

  const handleChange = (rawVal) => {
    const val = rawVal?.target ? rawVal.target.value : rawVal;
    setSliderVal(val === "" ? "" : Number(val));
    onChange?.(val);
  };

  return (
    <div
      className={`slider-card${disabled ? " slider-card--disabled" : ""}${showDivider ? " slider-card--divider" : ""}`}
    >
      <div className="slider-card-row">
        {showCheckbox ? (
          <CustomRadioCheck
            type="checkbox"
            id={id}
            label={label}
            checked={checked}
            onChange={() => onCheckChange?.(!checked)}
            disabled={checkboxDisabled}
          />
        ) : (
          <WPTypography
            variant={typographyVariants.tableData}
            asElement="div"
            className="slider-card-label"
          >
            {label}
          </WPTypography>
        )}

        <div className="wp-input-container">
          <WPInputTooltipError
            value={sliderVal}
            onChange={handleChange}
            min={min}
            max={max}
            integer={isInteger}
            maxLength={maxLength}
            disabled={disabled}
            ranges={{ hard: { min, max } }}
            validate={validateSliderRange}
            required
            requiredMsg={t("ValueStrings.requiredField")}
          />
        </div>
      </div>

      <div className="slider-card-scale">
        <WPTypography
          variant={typographyVariants.tableData}
          className={`scale-label scale-label-start ${disabled ? "disabled" : ""}`}
        >
          <ArrowRightIcon style={{ transform: "rotate(180deg)" }} />
          {leftLabel || t("advanceSettings.sliderLabelStart")}
        </WPTypography>
        <WPTypography
          variant={typographyVariants.tableData}
          className={`scale-label scale-label-end ${disabled ? "disabled" : ""}`}
        >
          {rightLabel || t("advanceSettings.sliderLabelEnd")}
          <ArrowRightIcon />
        </WPTypography>
      </div>

      <div className="slider-card-slider">
        <WPSlider
          min={min}
          max={max}
          displayMin={formattedMin}
          displayMax={formattedMax}
          value={sliderVal}
          onChange={handleChange}
          disabled={disabled}
          step={isInteger ? 1 : "any"}
        />
      </div>

      {recommended != null && (
        <div className="slider-card-recommended">
          <WPTypography
            variant={typographyVariants.tableData}
            className={`scale-label scale-label-middle ${disabled ? "disabled" : ""}`}
          >
            {`${t("advanceSettings.sliderLabelMiddle")}: ${isInteger ? Math.round(recommended) : parseFloat(recommended).toFixed(2)}`}
          </WPTypography>
        </div>
      )}
    </div>
  );
};

export default SliderCard;
